/**
 * SkinEditor.js
 * 3D-erster Skin Editor:
 * - Primaer: 3D-Modell, direkt drauf malen (Skin3DPainter)
 * - Sekundaer: 2D UV-Map Canvas (zum Feintuning)
 * - Tools: Pinsel, Radiergummi, Fuellen, Pipette
 * - Undo/Redo (History)
 * - Vanilla-Templates (Steve/Alex)
 * - Speichern / Download
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { api, getAuthToken } from '../services/api';
import { useToastContext } from '../App';
import ColorPicker from '../components/pixel-editor/ColorPicker';
import PixelCanvas from '../components/pixel-editor/PixelCanvas';
import { usePixelHistory } from '../components/pixel-editor/usePixelHistory';
import Skin3DPainter from '../components/3d-editor/Skin3DPainter';

const TOOLS = [
  { id: 'pencil',  icon: '✏️',  label: 'Pinsel'      },
  { id: 'eraser',  icon: '🧹', label: 'Radiergummi' },
  { id: 'fill',    icon: '🪣',  label: 'Fuellen'     },
  { id: 'picker',  icon: '💉',  label: 'Pipette'     },
];

/**
 * Laedt ein Bild von einer URL und liefert ein ImageData-Objekt (64x64).
 * withAuth=true: sendet den JWT-Token im Authorization-Header (fuer eigene Skins).
 * withAuth=false: normaler Fetch ohne Auth (fuer oeffentliche Endpunkte).
 */
async function fetchImageData(url, withAuth = false) {
  const headers = {};
  if (withAuth) {
    const token = getAuthToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  const resp = await fetch(url, { headers });
  if (!resp.ok) throw new Error(`Bild konnte nicht geladen werden (${resp.status})`);
  const blob = await resp.blob();
  const bmp = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = 64; canvas.height = 64;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bmp, 0, 0, 64, 64);
  bmp.close();
  return ctx.getImageData(0, 0, 64, 64);
}

/** Laedt ein File-Objekt (PNG) und liefert ImageData (64x64). */
async function fileToImageData(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = 64; canvas.height = 64;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, 64, 64);
          resolve(ctx.getImageData(0, 0, 64, 64));
        };
        img.onerror = reject;
        img.src = ev.target.result;
      } catch (e) { reject(e); }
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/** Wandelt ImageData in einen base64-PNG-DataURL um. */
function imageDataToDataUrl(imgData) {
  const canvas = document.createElement('canvas');
  canvas.width = 64; canvas.height = 64;
  canvas.getContext('2d').putImageData(imgData, 0, 0);
  return canvas.toDataURL('image/png');
}

function SkinEditor() {
  const navigate = useNavigate();
  const { id: skinId } = useParams();
  const location = useLocation();
  const addToast = useToastContext();

  // ── Editor-State ──────────────────────────────
  const [viewMode, setViewMode] = useState('3d'); // '3d' | '2d'
  const [tool, setTool]       = useState('pencil');
  const [color, setColor]     = useState([255, 255, 255, 255]);
  const [brushSize, setBrushSize] = useState(1);
  const [recentColors, setRecentColors] = useState([]);
  const [showGrid, setShowGrid] = useState(true);
  const [zoom, setZoom]        = useState(8);

  // ── Skin-Meta ─────────────────────────────────
  const [skinName, setSkinName]   = useState('Mein Skin');
  const [skinModel, setSkinModel] = useState('classic');
  const [description, setDescription] = useState('');
  const [saving, setSaving]   = useState(false);
  const [loading, setLoading] = useState(!!skinId);

  // ── Skin-Bilddaten als ImageData ──────────────
  // Skin3DPainter und PixelCanvas erhalten ImageData, keine URL
  const [skinImageData, setSkinImageData] = useState(null);

  // ── Refs ──────────────────────────────────────
  const painter3dRef = useRef(null);  // Skin3DPainter
  const canvas2dRef  = useRef(null);  // PixelCanvas (2D UV-Ansicht)
  const history = usePixelHistory();

  // ── Hilfsfunktion: ImageData setzen und ueberall aktualisieren ──
  const applyImageData = useCallback((imgData, pushHistory = true) => {
    setSkinImageData(imgData);
    if (pushHistory) history.push(imgData);
    if (canvas2dRef.current) canvas2dRef.current.setImageData(imgData);
    // Skin3DPainter erhaelt imageData als Prop — kein manueller Aufruf noetig,
    // ABER fuer sofortiges Update ohne Re-Render nutzen wir updateImageData:
    if (painter3dRef.current) painter3dRef.current.updateImageData(imgData);
  }, [history]);

  // ── Beim Start: Skin laden oder Template verwenden ──
  useEffect(() => {
    let cancelled = false;
    async function init() {
      setLoading(true);
      try {
        if (skinId) {
          // Vorhandenen Skin laden (benoetigt Auth)
          const meta = await api.getSkin(skinId);
          if (cancelled) return;
          setSkinName(meta.name);
          setSkinModel(meta.model || 'classic');
          setDescription(meta.description || '');
          const imgData = await fetchImageData(
            api.getSkinImageUrl(skinId) + '?t=' + Date.now(),
            true  // mit Auth
          );
          if (cancelled) return;
          setSkinImageData(imgData);
          history.push(imgData);
        } else if (location.state?.templateUrl) {
          // Vanilla-Template (oeffentlicher Endpunkt, kein Auth noetig)
          const model = location.state.model || 'classic';
          setSkinModel(model);
          const imgData = await fetchImageData(location.state.templateUrl, false);
          if (cancelled) return;
          setSkinImageData(imgData);
          history.push(imgData);
        } else {
          // Neuer leerer Skin — Steve als Basis
          const imgData = await fetchImageData(api.getDefaultSkinImageUrl('steve'), false);
          if (cancelled) return;
          setSkinImageData(imgData);
          history.push(imgData);
        }
      } catch (err) {
        if (cancelled) return;
        addToast('Fehler beim Laden: ' + err.message, 'error');
        if (skinId) navigate('/skins');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    init();
    return () => { cancelled = true; };
  }, [skinId]); // eslint-disable-line

  // ── 3D → 2D sync: wenn 3D-Painter etwas malt (kein History-Push) ──────────
  const handlePaint3D = useCallback((imgData) => {
    setSkinImageData(imgData);
    if (canvas2dRef.current) canvas2dRef.current.setImageData(imgData);
  }, []);

  // ── 2D → 3D sync: wenn 2D-Canvas etwas malt (kein History-Push) ──────────
  const handlePaint2D = useCallback((imgData) => {
    setSkinImageData(imgData);
    if (painter3dRef.current) painter3dRef.current.updateImageData(imgData);
  }, []);

  // ── History-Push nur bei Strich-Ende ─────────────────────────────────────
  const handleStrokeEnd = useCallback((imgData) => {
    history.push(imgData);
    setSkinImageData(imgData);
  }, [history]);

  // ── Undo / Redo ──────────────────────────────
  function handleUndo() {
    const prev = history.undo();
    if (!prev) return;
    setSkinImageData(prev);
    if (canvas2dRef.current)  canvas2dRef.current.setImageData(prev);
    if (painter3dRef.current) painter3dRef.current.updateImageData(prev);
  }

  function handleRedo() {
    const next = history.redo();
    if (!next) return;
    setSkinImageData(next);
    if (canvas2dRef.current)  canvas2dRef.current.setImageData(next);
    if (painter3dRef.current) painter3dRef.current.updateImageData(next);
  }

  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.ctrlKey && e.key === 'z') { e.preventDefault(); handleUndo(); }
      if (e.ctrlKey && (e.key === 'y' || (e.shiftKey && e.key === 'Z'))) { e.preventDefault(); handleRedo(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  // ── Farbe aufnehmen (Pipette) ────────────────
  const handleColorPick = useCallback((c) => {
    setColor(c);
    setTool('pencil');
    handleAddRecentColor(c);
  }, []);

  function handleAddRecentColor(c) {
    setRecentColors(prev => {
      const filtered = prev.filter(rc => !(rc[0]===c[0]&&rc[1]===c[1]&&rc[2]===c[2]&&rc[3]===c[3]));
      return [c, ...filtered].slice(0, 16);
    });
  }

  // ── PNG laden ────────────────────────────────
  async function handleLoadFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    try {
      const imgData = await fileToImageData(file);
      applyImageData(imgData, true);
    } catch (err) {
      addToast('Datei konnte nicht geladen werden: ' + err.message, 'error');
    }
  }

  // ── Vanilla-Template laden ────────────────────
  async function handleLoadTemplate(name) {
    try {
      const imgData = await fetchImageData(api.getDefaultSkinImageUrl(name), false);
      applyImageData(imgData, true);
      if (name === 'alex') setSkinModel('slim');
      else setSkinModel('classic');
    } catch (err) {
      addToast('Vorlage konnte nicht geladen werden: ' + err.message, 'error');
    }
  }

  // ── Aktuellen ImageData holen (3D oder 2D) ──
  function getCurrentImageData() {
    // 3D-Painter hat intern den aktuellsten Stand
    if (viewMode === '3d' && painter3dRef.current) {
      return painter3dRef.current.getImageData
        ? painter3dRef.current.getImageData()
        : skinImageData;
    }
    if (canvas2dRef.current) return canvas2dRef.current.getImageData();
    return skinImageData;
  }

  // ── Speichern ────────────────────────────────
  async function handleSave() {
    setSaving(true);
    try {
      const imgData = getCurrentImageData() || skinImageData;
      if (!imgData) throw new Error('Kein Canvas-Inhalt');
      const dataUrl = imageDataToDataUrl(imgData);

      if (skinId) {
        await api.updateSkin(skinId, { name: skinName, description, model: skinModel, skinDataBase64: dataUrl });
        addToast('Skin gespeichert!', 'success');
      } else {
        const created = await api.createSkin({ name: skinName, description, model: skinModel, skinDataBase64: dataUrl });
        addToast('Skin erstellt!', 'success');
        navigate(`/skins/editor/${created.id}`, { replace: true });
      }
    } catch (err) {
      addToast('Fehler beim Speichern: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  function handleDownload() {
    const imgData = getCurrentImageData() || skinImageData;
    if (!imgData) return;
    const dataUrl = imageDataToDataUrl(imgData);
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `${skinName.replace(/[^a-zA-Z0-9_-]/g, '_')}.png`;
    a.click();
  }

  if (loading) {
    return <div className="page-content"><div className="empty-state"><div className="spinner" /></div></div>;
  }

  return (
    <div className="skin-editor-layout">
      {/* ── Header ── */}
      <div className="skin-editor-header">
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/skins')}>
          ← Bibliothek
        </button>

        <div className="skin-editor-title-row">
          <input
            className="mc-input skin-name-input"
            value={skinName}
            onChange={e => setSkinName(e.target.value)}
            placeholder="Skin-Name..."
            maxLength={64}
          />
          <select
            className="mc-select"
            value={skinModel}
            onChange={e => setSkinModel(e.target.value)}
          >
            <option value="classic">Classic (Steve, 4px Arme)</option>
            <option value="slim">Slim (Alex, 3px Arme)</option>
          </select>
        </div>

        {/* 3D / 2D Umschalten */}
        <div className="skin-editor-view-toggle">
          <button
            className={`btn btn-sm ${viewMode === '3d' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('3d')}
            title="3D-Maler"
          >
            3D
          </button>
          <button
            className={`btn btn-sm ${viewMode === '2d' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('2d')}
            title="UV-Karte (2D)"
          >
            2D UV
          </button>
        </div>

        <div className="skin-editor-header-actions">
          <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
            PNG laden
            <input type="file" accept=".png" style={{ display: 'none' }} onChange={handleLoadFile} />
          </label>
          <button className="btn btn-secondary btn-sm" onClick={handleDownload}>⬇ Download</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Speichert...' : (skinId ? '💾 Speichern' : '+ Erstellen')}
          </button>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="skin-editor-body">

        {/* ── Linke Sidebar: Tools ── */}
        <div className="skin-editor-sidebar-left">

          {/* Tools */}
          <div className="skin-editor-panel">
            <div className="panel-title">Werkzeug</div>
            <div className="skin-tools-grid">
              {TOOLS.map(t => (
                <button
                  key={t.id}
                  className={`skin-tool-btn ${tool === t.id ? 'active' : ''}`}
                  onClick={() => setTool(t.id)}
                  title={t.label}
                >
                  <span style={{ fontSize: 20 }}>{t.icon}</span>
                  <span style={{ fontSize: 10 }}>{t.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Pinselgroesse */}
          <div className="skin-editor-panel">
            <div className="panel-title">Pinselgroesse: {brushSize}px</div>
            <input
              type="range" min={1} max={5} value={brushSize}
              onChange={e => setBrushSize(Number(e.target.value))}
              style={{ width: '100%' }}
            />
          </div>

          {/* Undo / Redo */}
          <div className="skin-editor-panel">
            <div className="panel-title">Verlauf</div>
            <div className="flex gap-2">
              <button className="btn btn-secondary btn-sm" onClick={handleUndo} disabled={!history.canUndo} style={{ flex: 1 }}>
                ↩ Undo
              </button>
              <button className="btn btn-secondary btn-sm" onClick={handleRedo} disabled={!history.canRedo} style={{ flex: 1 }}>
                Redo ↪
              </button>
            </div>
          </div>

          {/* Hinweise je nach Modus */}
          <div className="skin-editor-panel">
            {viewMode === '3d' ? (
              <div className="text-sm text-muted" style={{ lineHeight: 1.7 }}>
                <strong>3D-Maler Steuerung:</strong><br />
                Linksklick = Malen<br />
                Rechtsklick + Ziehen = Drehen<br />
                Shift + Klick = Drehen<br />
                Scrollrad = Zoom<br />
              </div>
            ) : (
              <div className="text-sm text-muted" style={{ lineHeight: 1.7 }}>
                <strong>2D UV-Karte:</strong><br />
                Direkt auf dem 64x64<br />
                Skin-Template malen.<br />
                Grid: <button
                  className={`btn btn-sm ${showGrid ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ padding: '2px 8px', marginLeft: 4 }}
                  onClick={() => setShowGrid(v => !v)}
                >
                  {showGrid ? 'An' : 'Aus'}
                </button>
              </div>
            )}
          </div>

          {/* Farbe */}
          <div className="skin-editor-panel">
            <div className="panel-title">Farbe</div>
            <ColorPicker
              color={color}
              onChange={setColor}
              recentColors={recentColors}
              onAddRecent={handleAddRecentColor}
            />
          </div>
        </div>

        {/* ── Mittlerer Bereich: 3D oder 2D Editor ── */}
        <div className="skin-editor-canvas-area" style={{ position: 'relative' }}>
          {/* 3D-Maler */}
          <div style={{
            display: viewMode === '3d' ? 'flex' : 'none',
            flex: 1, height: '100%', position: 'relative',
          }}>
            {skinImageData && (
              <Skin3DPainter
                ref={painter3dRef}
                imageData={skinImageData}
                model={skinModel}
                tool={tool}
                color={color}
                brushSize={brushSize}
                onPaint={handlePaint3D}
                onStrokeEnd={handleStrokeEnd}
                onColorPick={handleColorPick}
              />
            )}
          </div>

          {/* 2D UV-Karte */}
          <div style={{
            display: viewMode === '2d' ? 'block' : 'none',
            overflow: 'auto',
            flex: 1,
            padding: 16,
            background: '#111',
          }}>
            <div className="text-sm text-muted" style={{ marginBottom: 8 }}>
              64x64 UV-Skin-Karte — Zoom:
              <select
                className="mc-select"
                style={{ marginLeft: 8, padding: '2px 6px' }}
                value={zoom}
                onChange={e => setZoom(Number(e.target.value))}
              >
                {[4, 6, 8, 10, 12].map(z => (
                  <option key={z} value={z}>{z}x</option>
                ))}
              </select>
            </div>
            <PixelCanvas
              ref={canvas2dRef}
              width={64}
              height={64}
              zoom={zoom}
              tool={tool}
              color={color}
              showGrid={showGrid}
              onPixelChange={handlePaint2D}
              onStrokeEnd={handleStrokeEnd}
              onColorPick={handleColorPick}
            />
          </div>
        </div>

        {/* ── Rechte Sidebar: Beschreibung ── */}
        <div className="skin-editor-sidebar-right">
          <div className="skin-editor-panel">
            <div className="panel-title">Beschreibung</div>
            <textarea
              className="mc-input"
              style={{ width: '100%', minHeight: 60, resize: 'vertical' }}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Optional..."
              maxLength={256}
            />
          </div>

          {/* Vanilla Templates */}
          <div className="skin-editor-panel">
            <div className="panel-title">Vorlage laden</div>
            <div className="flex gap-2" style={{ flexDirection: 'column' }}>
              {['steve', 'alex'].map(name => (
                <button
                  key={name}
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleLoadTemplate(name)}
                >
                  {name === 'alex' ? 'Alex (Slim)' : 'Steve (Classic)'} laden
                </button>
              ))}
            </div>
          </div>

          {/* Skin-Modell Info */}
          <div className="skin-editor-panel">
            <div className="panel-title">Modell</div>
            <div className="flex gap-2">
              {['classic', 'slim'].map(m => (
                <button
                  key={m}
                  className={`btn btn-sm ${skinModel === m ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setSkinModel(m)}
                  style={{ flex: 1 }}
                >
                  {m === 'classic' ? 'Steve' : 'Alex'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SkinEditor;
